/**
 * The language a company's customers (homeowners) see (docs/features/homeowner-language.md; owner decision 20 Oct):
 *
 * - Branding › Brand basics: "Language your customers see" (their own language, the default, or always one language)
 *   and "Your main language" (the fallback; from the company's country until chosen). Saved at once
 *   (PUT /v1/orgs/{org_id}/homeowner-language, audited).
 * - The lead panel's "Customer's language" row: who decided it and a change control (PUT …/public-quotes/{id}/locale,
 *   audited).
 * - The internal-copy dialog: any of the 50 for the installer's own copy of the proposal or the quote page
 *   (`?copy=internal&locale=`): marked, never sent and never signed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe } from "lucide-react";
import { useId, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrganization } from "../api/queries";
import { Button, Field, Modal, useToast } from "../components/ui";
import { useT } from "./I18nProvider";
import { HOMEOWNER_LOCALES, localeInfo } from "./negotiate.ts";

type Code = S["HomeownerLocaleCode"];

/** "Deutsch · German": a language's own name, then its English one when they differ. */
export function languageName(code: string): string {
  const info = localeInfo(code);
  return info.name === info.english ? info.name : `${info.name} · ${info.english}`;
}

/** A select of the 50 homeowner languages, each in its own name. */
export function LanguageSelect({ value, onChange, disabled, label, testId, empty }: {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  label?: string;
  testId?: string;
  empty?: string;
}) {
  return (
    <select className="select" value={value} disabled={disabled} aria-label={label} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {HOMEOWNER_LOCALES.map((l) => (
        <option key={l.code} value={l.code} lang={l.code}>
          {languageName(l.code)}
        </option>
      ))}
    </select>
  );
}

/** Branding › Brand basics: "Language your customers see" and "Your main language". */
export function HomeownerLanguageField({ orgId, disabled }: { orgId: string; disabled?: boolean }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const name = useId();
  const company = useOrganization(orgId);
  const org = company.data;
  const save = useMutation({
    mutationFn: (body: S["HomeownerLanguageWrite"]) =>
      unwrap(api.PUT("/v1/orgs/{org_id}/homeowner-language", { params: { path: { org_id: orgId } }, body })),
    onSuccess: (updated) => {
      qc.setQueryData(["org", orgId], updated);
      // "Write my own text" starts from our texts in the main language
      void qc.invalidateQueries({ queryKey: ["brand-default-texts", orgId] });
      toast(t("homeowner.branding.saved"));
    },
    onError: (err) => toast(errorMessage(err), true),
  });
  if (!org) return null;
  const mode = org.homeowner_locale_mode ?? "auto";
  const main = org.main_locale ?? "en";
  const fixed = org.homeowner_locale ?? main;
  const busy = disabled || save.isPending;
  const write = (next: Partial<{ mode: "auto" | "fixed"; locale: string; main: string | null }>) =>
    save.mutate({
      mode: next.mode ?? mode,
      locale: ((next.mode ?? mode) === "fixed" ? next.locale ?? fixed : null) as Code | null,
      main_locale: (next.main !== undefined ? next.main : org.default_locale ?? null) as Code | null,
    });

  return (
    <>
      <fieldset className="field span-2 hl-mode" data-testid="customer-language" disabled={busy}>
        <legend>{t("homeowner.branding.title")}</legend>
        <label className="hl-option">
          <input type="radio" name={name} value="auto" checked={mode === "auto"} onChange={() => write({ mode: "auto" })} />
          <span>
            <b>{t("homeowner.branding.auto")}</b>
            <span className="hint">{t("homeowner.branding.autoHelp")}</span>
          </span>
        </label>
        <label className="hl-option">
          <input type="radio" name={name} value="fixed" checked={mode === "fixed"} onChange={() => write({ mode: "fixed" })} />
          <span>
            <b>{t("homeowner.branding.fixed")}</b>
            <span className="hint">{t("homeowner.branding.fixedHelp")}</span>
          </span>
        </label>
        {mode === "fixed" && (
          <div className="hl-fixed">
            <LanguageSelect value={fixed} onChange={(code) => write({ locale: code })} disabled={busy}
              label={t("homeowner.branding.fixedLanguage")} testId="customer-language-fixed" />
          </div>
        )}
        <p className="hint hl-switch"><Globe size={13} aria-hidden /> {t("homeowner.branding.canSwitch")}</p>
      </fieldset>
      <Field label={t("homeowner.branding.main")} hint={t("homeowner.branding.mainHelp", { language: localeInfo(main).name })}
        className="span-2">
        <LanguageSelect value={org.default_locale ?? ""} onChange={(code) => write({ main: code || null })} disabled={busy}
          testId="main-language" empty={t("homeowner.branding.fromCountry", { language: languageName(main) })} />
      </Field>
    </>
  );
}

const SOURCES = {
  customer: "homeowner.lead.source.customer",
  detected: "homeowner.lead.source.detected",
  installer: "homeowner.lead.source.installer",
  company: "homeowner.lead.source.company",
} as const;

/** The lead panel's "Customer's language: Español (Latinoamérica) · chosen by the customer" with a change control. */
export function LeadLanguageRow({ orgId, publicQuoteId, offer, canEdit }: {
  orgId: string;
  publicQuoteId: string;
  offer: S["QuoteOffer"];
  canEdit: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const locale = offer.locale ?? "en";
  const [choice, setChoice] = useState<string>(locale);
  const save = useMutation({
    mutationFn: (code: string) =>
      unwrap(api.PUT("/v1/orgs/{org_id}/public-quotes/{quote_id}/locale", {
        params: { path: { org_id: orgId, quote_id: publicQuoteId } }, body: { locale: code as Code },
      })),
    onSuccess: (updated) => {
      qc.setQueryData(["lead-offer", orgId, publicQuoteId], updated);
      qc.invalidateQueries({ queryKey: ["lead-activity", orgId, publicQuoteId] });
      setEditing(false);
      toast(t("homeowner.lead.saved", { language: localeInfo(updated.offer.locale ?? choice).name }));
    },
    onError: (err) => toast(errorMessage(err), true),
  });
  const source = SOURCES[offer.locale_source ?? "company"];
  return (
    <div className="hl-lead small" data-testid="lead-language">
      <Globe size={14} className="faint" aria-hidden />
      <span>{t("homeowner.lead.label")}:</span>
      {editing ? (
        <span className="hl-lead-edit">
          <LanguageSelect value={choice} onChange={setChoice} label={t("homeowner.lead.label")} testId="lead-language-select" />
          <Button size="sm" variant="primary" loading={save.isPending} disabled={choice === locale && offer.locale_source === "installer"}
            onClick={() => save.mutate(choice)}>
            {t("homeowner.lead.save")}
          </Button>
          <Button size="sm" onClick={() => setEditing(false)}>{t("homeowner.lead.cancel")}</Button>
          <span className="hint">{t("homeowner.lead.changeHelp")}</span>
        </span>
      ) : (
        <>
          <b lang={locale}>{localeInfo(locale).name}</b>
          <span className="muted">· {t(source)}</span>
          {canEdit && (
            <button type="button" className="link-btn" onClick={() => { setChoice(locale); setEditing(true); }}
              data-testid="lead-language-change">
              {t("homeowner.lead.change")}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Picks one of the 50 for the installer's internal copy of the proposal or of the quote page. */
export function InternalCopyDialog({ kind, initial, busy, onConfirm, onClose }: {
  kind: "proposal" | "page";
  initial?: string;
  busy?: boolean;
  onConfirm: (code: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const help = useId();
  const [code, setCode] = useState(initial ?? "en");
  return (
    <Modal title={t(kind === "proposal" ? "homeowner.proposal.copyTitle" : "homeowner.proposal.pageTitle")} onClose={onClose}
      describedBy={help} width={460}
      footer={
        <>
          <Button onClick={onClose}>{t("homeowner.lead.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={() => onConfirm(code)} data-testid="internal-copy-confirm">
            {t(kind === "proposal" ? "homeowner.proposal.downloadCopy" : "homeowner.proposal.openPage")}
          </Button>
        </>
      }>
      <Field label={t("homeowner.proposal.language")}>
        <LanguageSelect value={code} onChange={setCode} testId="internal-copy-language" />
      </Field>
      <p className="hint" id={help}>{t("homeowner.proposal.internalHelp")}</p>
    </Modal>
  );
}
